import { Users } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useCallContext } from "./CallContext";

interface ParticipantRoleAssignment {
  recallParticipantId: string;
  role: "salesperson" | "client" | "observer";
  displayName: string;
  isCurrentUser?: boolean;
  assignedBy?: string;
  assignedAt?: string;
}

interface ParticipantInfo {
  id: number;
  name: string;
  role: "salesperson" | "client" | "observer";
  isHost: boolean;
  hasExplicitAssignment: boolean;
}

interface ParticipantRolesResponse {
  assignments: ParticipantRoleAssignment[];
  participants: ParticipantInfo[];
}

export function ParticipantManagement() {
  const { roomBot, botSocket, participantRoles } = useCallContext();
  const [isLoading, setIsLoading] = useState(false);

  const meetingParticipants = roomBot?.meeting_participants || [];

  // Request current participant roles when component mounts
  useEffect(() => {
    if (botSocket?.send) {
      botSocket.send(
        JSON.stringify({
          type: "GET_PARTICIPANT_ROLES",
          payload: {},
          timestamp: new Date().toISOString(),
        }),
      );
    }
  }, [botSocket]);

  const handleRoleAssignment = async (
    participantId: number,
    newRole: "salesperson" | "client" | "observer",
  ) => {
    if (!botSocket?.send) return;

    const participant = meetingParticipants.find((p) => p.id === participantId);
    if (!participant) return;

    console.log("🔄 Assigning role:", {
      participantId,
      newRole,
      participantName: participant.name,
    });
    console.log("🔄 Current participantRoles:", participantRoles);

    setIsLoading(true);

    try {
      botSocket.send(
        JSON.stringify({
          type: "ASSIGN_PARTICIPANT_ROLE",
          payload: {
            recallParticipantId: participantId.toString(),
            role: newRole,
            displayName: participant.name,
          },
          timestamp: new Date().toISOString(),
        }),
      );

      console.log("✅ Role assignment message sent");
    } catch (error) {
      console.error("❌ Error assigning participant role:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getParticipantRole = (
    participantId: number,
  ): "salesperson" | "client" | "observer" => {
    const participantInfo = participantRoles?.participants.find(
      (p) => p.id === participantId,
    );
    const role = participantInfo?.role || "client";

    console.log("🔍 Getting role for participant", participantId, ":", role, {
      participantInfo,
      participantRoles,
    });

    return role;
  };

  const hasExplicitAssignment = (participantId: number): boolean => {
    return (
      participantRoles?.participants.find((p) => p.id === participantId)
        ?.hasExplicitAssignment || false
    );
  };

  if (meetingParticipants.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center text-gray-500">
          <Users className="mx-auto mb-4 h-12 w-12" />
          <p>No meeting participants detected yet.</p>
          <p className="text-sm">
            Participants will appear here once the bot joins the call.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-4">
      <div className="mb-6">
        <h2 className="mb-2 text-xl font-semibold">Meeting Participants</h2>
        <p className="text-sm text-gray-600">
          Assign roles to participants to improve script detection and AI
          assistance accuracy.
        </p>
      </div>

      <div className="space-y-4">
        {meetingParticipants.map((participant) => (
          <Card key={participant.id} className="p-4">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center justify-between text-base">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  <span>{participant.name}</span>
                  {participant.is_host && (
                    <span className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-800">
                      Host
                    </span>
                  )}
                </div>
                {hasExplicitAssignment(participant.id) && (
                  <span className="rounded bg-green-100 px-2 py-1 text-xs text-green-800">
                    Assigned
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label
                    htmlFor={`role-${participant.id}`}
                    className="text-sm font-medium"
                  >
                    Role
                  </Label>
                  <Select
                    value={getParticipantRole(participant.id)}
                    onValueChange={(
                      value: "salesperson" | "client" | "observer",
                    ) => handleRoleAssignment(participant.id, value)}
                    disabled={isLoading}
                  >
                    <SelectTrigger id={`role-${participant.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="salesperson">Salesperson</SelectItem>
                      <SelectItem value="client">Customer</SelectItem>
                      <SelectItem value="observer">Observer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <div className="text-xs text-gray-500">
                    <div>Platform ID: {participant.id}</div>
                    <div>
                      Current role: {getParticipantRole(participant.id)}
                      {!hasExplicitAssignment(participant.id) &&
                        " (auto-detected)"}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-6 rounded-lg bg-blue-50 p-4">
        <h3 className="mb-2 font-medium text-blue-900">Role Assignment Tips</h3>
        <ul className="space-y-1 text-sm text-blue-800">
          <li>
            • <strong>Salesperson:</strong> Used for script detection and
            completion tracking
          </li>
          <li>
            • <strong>Customer:</strong> Used for question detection and AI help
            suggestions
          </li>
          <li>
            • <strong>Observer:</strong> Ignored by script and question
            detection systems
          </li>
          <li>• Roles persist for the duration of this call session</li>
        </ul>
      </div>
    </div>
  );
}
